import { useSortable } from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import Icon from '../../components/Icon';
import { Badge } from '../../components/ui';
import { FIELD_ICON, TYPE_LABEL } from './fieldMeta';
import type { FormField } from '../../types';

interface Props {
  field: FormField;
  selected: boolean;
  error?: string;
  /** True once this key exists in a published version: type is locked. */
  published: boolean;
  onSelect: () => void;
  onRemove: () => void;
}

export default function FieldRow({
  field, selected, error, published, onSelect, onRemove,
}: Props) {
  const {
    attributes, listeners, setNodeRef, transform, transition, isDragging,
  } = useSortable({ id: field.key });

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
  };

  return (
    <div
      ref={setNodeRef}
      style={style}
      data-field-key={field.key}
      className={[
        'field-row',
        selected ? 'selected' : '',
        isDragging ? 'dragging' : '',
        field.system ? 'system' : '',
      ].filter(Boolean).join(' ')}
    >
      <button
        type="button"
        className="drag-handle"
        aria-label={`Reorder ${field.label}`}
        {...attributes}
        {...listeners}
      >
        <Icon name="drag" size={16} />
      </button>

      <div className="palette-icon" aria-hidden="true">
        <Icon name={FIELD_ICON[field.type]} size={15} />
      </div>

      <div
        className="field-row-main"
        role="button"
        tabIndex={0}
        onClick={onSelect}
        onKeyDown={(e) => {
          if (e.key === 'Enter' || e.key === ' ') {
            e.preventDefault();
            onSelect();
          }
        }}
      >
        <div className="field-row-label">
          {field.label || <span className="faint">Untitled field</span>}
        </div>
        <div className="field-row-meta">
          <span>{TYPE_LABEL[field.type]}</span>
          <span className="field-key">{field.key}</span>
          {error ? <span style={{ color: 'var(--danger)', fontWeight: 600 }}>{error}</span> : null}
        </div>
      </div>

      <div className="row gap-4">
        {field.required ? <Badge tone="brand">Required</Badge> : null}
        {field.showInList ? (
          <span title="Shown as a column in the customer list" className="faint">
            <Icon name="eye" size={15} />
          </span>
        ) : null}

        {field.system ? (
          <span className="badge badge-neutral" title="Always on the form">Built-in</span>
        ) : (
          <button
            type="button"
            className="icon-btn danger"
            title={published
              ? 'Remove from the form — values already captured are kept'
              : 'Remove this field'}
            aria-label={`Remove ${field.label}`}
            onClick={onRemove}
          >
            <Icon name="trash" size={15} />
          </button>
        )}
      </div>
    </div>
  );
}
